import { toBaseUnits } from './journal';
import type { Product, PurchaseLine } from './types';

/**
 * What a delivery does to a product: stock in, cost re-averaged.
 *
 * The first place in the app that *increases* stock — until now `remaining` only
 * ever went down, at checkout, and nothing could put goods back.
 */

export interface PurchaseEffect {
  /** New stock in base units, or undefined for an untracked product. */
  remaining: number | undefined;
  /** New cost per base unit, in LAK. */
  cost: number;
  /** What arrived, in base units — the amount that moves stock. */
  qtyBase: number;
  /** Cost of one base unit in this delivery, before averaging. */
  unitCostBase: number;
}

/**
 * Applies one purchase line to a product.
 *
 * Cost is a **moving weighted average**, not last-cost-wins. The shop sells from
 * mixed stock, so pricing today's sale at today's delivery cost makes the margin
 * swing on a single lorry-load. Averaging tracks what the goods on the floor
 * actually cost.
 *
 * This cannot corrupt history: cost of goods is snapshotted onto each sale when
 * it happens, so re-averaging never moves a past month's profit.
 */
export function applyPurchase(product: Product, line: PurchaseLine): PurchaseEffect {
  const qtyBase = toBaseUnits(product, line.unit, line.qty);
  const perBase = product.units.find((u) => u.unit === line.unit)?.perBase ?? 1;
  // A cost quoted per ໂຕນ has to become a cost per ຖົງ before it can be averaged
  // against a stock figure held in ຖົງ.
  const unitCostBase = perBase > 0 ? line.unitCost / perBase : line.unitCost;

  const oldRemaining = product.remaining;
  const oldCost = product.cost;

  // Averaging needs both a quantity and a cost to average against. Missing
  // either — a new product, or one whose cost was never entered — means this
  // delivery simply sets the cost.
  const canAverage =
    oldRemaining !== undefined && oldRemaining > 0 && oldCost !== undefined && oldCost > 0;

  const cost = canAverage
    ? Math.round((oldRemaining * oldCost + qtyBase * unitCostBase) / (oldRemaining + qtyBase))
    : Math.round(unitCostBase);

  return {
    // An untracked product stays untracked: receiving goods is not the moment to
    // start counting stock the shop has chosen not to count.
    remaining: oldRemaining === undefined ? undefined : roundStock(oldRemaining + qtyBase),
    cost,
    qtyBase,
    unitCostBase: Math.round(unitCostBase),
  };
}

/** Line total in LAK, in the unit the goods were quoted in. */
export function purchaseLineTotal(line: PurchaseLine): number {
  return Math.round(line.unitCost * line.qty);
}

export function purchaseTotal(lines: PurchaseLine[]): number {
  return lines.reduce((sum, l) => sum + purchaseLineTotal(l), 0);
}

/** Stock can be fractional for bulk units; two places is as fine as it gets. */
export function roundStock(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Units a product can actually be received in.
 *
 * A unit with no `perBase` cannot be converted to the stock unit, so accepting a
 * delivery in it would silently add the wrong quantity. Only the base unit and
 * units with a known factor are offered.
 */
export function receivableUnits(product: Product): string[] {
  return product.units
    .filter((u, i) => i === 0 || (u.perBase !== undefined && u.perBase > 0))
    .map((u) => u.unit);
}
